/**
 * Org Signal Queue page — Lead Analyst review of gated signals (b2b-2-3)
 *
 * Routes: /org/signals/queue
 *
 * Features:
 * - Table of pending gated signals with symbol, direction, score, thesis
 * - Approve / Reject buttons per row
 * - Bulk-approve selected signals
 * - Signal action policy settings (org_admin only)
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { usePermissionContext } from '../../contexts/PermissionContext';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface SignalQueueItem {
  id: number;
  org_id: number;
  lead_id: number | null;
  submitted_by: number;
  symbol: string;
  exchange: string | null;
  direction: string;
  source: string;
  gate_score: number | null;
  strategy_matched: string | null;
  technical_rationale: string | null;
  thesis: string | null;
  catalyst: string | null;
  entry_price: number | null;
  stop_loss: number | null;
  take_profit: number | null;
  status: string;
  reviewed_by: number | null;
  reviewed_at: string | null;
  review_note: string | null;
  created_at: string | null;
}

interface SignalQueueListResponse {
  items: SignalQueueItem[];
  total: number;
}

interface SignalPolicyResponse {
  org_id: number;
  signal_action_policy: string;
}

// ---------------------------------------------------------------------------
// API helpers
// ---------------------------------------------------------------------------

const signalQueueApi = {
  list: (status?: string) => {
    const params = status ? `?status=${encodeURIComponent(status)}` : '';
    return api.request<SignalQueueListResponse>(`/api/v1/orgs/signals/queue${params}`);
  },
  approve: (id: number, note?: string) =>
    api.request<SignalQueueItem>(`/api/v1/orgs/signals/queue/${id}/approve`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note ?? null }),
    }),
  reject: (id: number, note?: string) =>
    api.request<SignalQueueItem>(`/api/v1/orgs/signals/queue/${id}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: note ?? null }),
    }),
  bulkApprove: (ids: number[], note?: string) =>
    api.request<{ approved: number; ids: number[] }>('/api/v1/orgs/signals/queue/bulk-approve', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ids, note: note ?? null }),
    }),
  getPolicy: () => api.request<SignalPolicyResponse>('/api/v1/orgs/signal-policy'),
  updatePolicy: (policy: string) =>
    api.request<SignalPolicyResponse>('/api/v1/orgs/signal-policy', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ signal_action_policy: policy }),
    }),
};

// ---------------------------------------------------------------------------
// Policy badge
// ---------------------------------------------------------------------------

const POLICY_INFO: Record<string, { label: string; color: string; description: string }> = {
  open: {
    label: 'Open',
    color: 'bg-emerald-900/30 text-emerald-400 border border-emerald-700/40',
    description: 'Any member can act on signals.',
  },
  notify_only: {
    label: 'Notify Only',
    color: 'bg-amber-900/30 text-amber-400 border border-amber-700/40',
    description: 'Members see signals but cannot mark acted-on.',
  },
  gated: {
    label: 'Gated',
    color: 'bg-purple-900/30 text-purple-400 border border-purple-700/40',
    description: 'Signals require Lead Analyst approval before members see them.',
  },
};

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const SignalQueue: React.FC = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { roles } = usePermissionContext();
  const isOrgAdmin = roles.includes('org_admin');
  const [statusFilter, setStatusFilter] = useState('pending');
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [editingPolicy, setEditingPolicy] = useState(false);
  const [newPolicy, setNewPolicy] = useState('');

  const { data: queueData, isLoading: queueLoading, error: queueError } = useQuery({
    queryKey: ['signal-queue', statusFilter],
    queryFn: () => signalQueueApi.list(statusFilter),
  });

  const { data: policyData } = useQuery({
    queryKey: ['signal-policy'],
    queryFn: signalQueueApi.getPolicy,
    enabled: !user?.is_admin,
  });

  const approveMutation = useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      signalQueueApi.approve(id, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signal-queue'] });
      toast.success('Signal approved');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to approve signal'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, note }: { id: number; note?: string }) =>
      signalQueueApi.reject(id, note),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signal-queue'] });
      toast.success('Signal rejected');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to reject signal'),
  });

  const bulkApproveMutation = useMutation({
    mutationFn: (ids: number[]) => signalQueueApi.bulkApprove(ids),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['signal-queue'] });
      setSelected(new Set());
      toast.success(`${data.approved} signals approved`);
    },
    onError: (err: Error) => toast.error(err.message || 'Bulk approve failed'),
  });

  const updatePolicyMutation = useMutation({
    mutationFn: (policy: string) => signalQueueApi.updatePolicy(policy),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signal-policy'] });
      setEditingPolicy(false);
      toast.success('Policy updated');
    },
    onError: (err: Error) => toast.error(err.message || 'Failed to update policy'),
  });

  const items = queueData?.items ?? [];
  const currentPolicy = policyData?.signal_action_policy ?? 'open';
  const policyInfo = POLICY_INFO[currentPolicy] ?? POLICY_INFO.open;

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === items.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(items.map((i) => i.id)));
    }
  };

  const toggleExpand = (id: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const directionColor = (d: string) =>
    d.toUpperCase() === 'BUY'
      ? 'text-emerald-400'
      : d.toUpperCase() === 'SELL'
      ? 'text-red-400'
      : 'text-slate-300';

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Signal Queue</h1>
          <p className="text-slate-400 text-sm mt-1">
            Review and approve gated signals before they reach members.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className={`px-3 py-1 rounded-full text-xs font-medium ${policyInfo.color}`}>
            Policy: {policyInfo.label}
          </span>
          {isOrgAdmin && (
            <button
              onClick={() => {
                setNewPolicy(currentPolicy);
                setEditingPolicy(true);
              }}
              className="px-3 py-1.5 text-xs rounded-md bg-slate-700 text-slate-300 hover:bg-slate-600 transition-colors"
            >
              Change Policy
            </button>
          )}
        </div>
      </div>

      {/* Policy editor */}
      {editingPolicy && (
        <div className="bg-slate-800 border border-slate-700 rounded-xl p-5 space-y-4">
          <h2 className="text-white font-semibold">Signal Action Policy</h2>
          <p className="text-slate-400 text-sm">{policyInfo.description}</p>
          <div className="space-y-2">
            {Object.entries(POLICY_INFO).map(([key, info]) => (
              <label key={key} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="radio"
                  name="policy"
                  value={key}
                  checked={newPolicy === key}
                  onChange={() => setNewPolicy(key)}
                  className="mt-0.5"
                />
                <div>
                  <span className={`text-sm font-medium px-2 py-0.5 rounded-full ${info.color}`}>
                    {info.label}
                  </span>
                  <p className="text-slate-400 text-xs mt-1">{info.description}</p>
                </div>
              </label>
            ))}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => updatePolicyMutation.mutate(newPolicy)}
              disabled={updatePolicyMutation.isPending}
              className="px-4 py-2 bg-teal-600 hover:bg-teal-500 text-white text-sm rounded-lg disabled:opacity-50"
            >
              {updatePolicyMutation.isPending ? 'Saving…' : 'Save Policy'}
            </button>
            <button
              onClick={() => setEditingPolicy(false)}
              className="px-4 py-2 bg-slate-700 text-slate-300 text-sm rounded-lg hover:bg-slate-600"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Filters + bulk actions */}
      <div className="flex items-center gap-3 flex-wrap">
        {['pending', 'approved', 'rejected', 'pending,approved,rejected'].map((f) => (
          <button
            key={f}
            onClick={() => setStatusFilter(f)}
            className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
              statusFilter === f
                ? 'bg-teal-600 border-teal-500 text-white'
                : 'bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-500'
            }`}
          >
            {f === 'pending,approved,rejected' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
          </button>
        ))}

        {selected.size > 0 && (
          <button
            onClick={() => bulkApproveMutation.mutate(Array.from(selected))}
            disabled={bulkApproveMutation.isPending}
            className="ml-auto px-4 py-1.5 text-xs rounded-md bg-emerald-700 hover:bg-emerald-600 text-white disabled:opacity-50"
          >
            {bulkApproveMutation.isPending
              ? 'Approving…'
              : `Approve Selected (${selected.size})`}
          </button>
        )}
      </div>

      {/* Queue table */}
      {queueLoading ? (
        <div className="text-slate-400 text-sm py-8 text-center">Loading queue…</div>
      ) : queueError ? (
        <div className="text-red-400 text-sm py-8 text-center">
          {(queueError as Error).message || 'Failed to load queue'}
        </div>
      ) : items.length === 0 ? (
        <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-12 text-center">
          <p className="text-slate-400 text-sm">No signals in queue.</p>
          {currentPolicy !== 'gated' && (
            <p className="text-slate-500 text-xs mt-2">
              Set policy to <strong>Gated</strong> to start holding signals for review.
            </p>
          )}
        </div>
      ) : (
        <div className="bg-slate-800 border border-slate-700 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700">
                <th className="w-10 p-3 text-left">
                  <input
                    type="checkbox"
                    checked={selected.size === items.length && items.length > 0}
                    onChange={toggleAll}
                    className="rounded"
                  />
                </th>
                <th className="p-3 text-left text-slate-400 font-medium">Symbol</th>
                <th className="p-3 text-left text-slate-400 font-medium">Direction</th>
                <th className="p-3 text-left text-slate-400 font-medium">Source</th>
                <th className="p-3 text-left text-slate-400 font-medium">Score</th>
                <th className="p-3 text-left text-slate-400 font-medium">Status</th>
                <th className="p-3 text-left text-slate-400 font-medium">Created</th>
                <th className="p-3 text-right text-slate-400 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <React.Fragment key={item.id}>
                  <tr
                    className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer"
                    onClick={() => toggleExpand(item.id)}
                  >
                    <td className="p-3" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selected.has(item.id)}
                        onChange={() => toggleSelect(item.id)}
                        disabled={item.status !== 'pending'}
                        className="rounded"
                      />
                    </td>
                    <td className="p-3">
                      <span className="text-white font-medium">{item.symbol}</span>
                      {item.exchange && (
                        <span className="text-slate-500 text-xs ml-1">{item.exchange}</span>
                      )}
                    </td>
                    <td className="p-3">
                      <span className={`font-medium ${directionColor(item.direction)}`}>
                        {item.direction.toUpperCase()}
                      </span>
                    </td>
                    <td className="p-3 text-slate-300 capitalize">{item.source}</td>
                    <td className="p-3 text-slate-300">
                      {item.gate_score != null ? item.gate_score.toFixed(0) : '—'}
                    </td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                          item.status === 'pending'
                            ? 'bg-amber-900/40 text-amber-400'
                            : item.status === 'approved'
                            ? 'bg-emerald-900/40 text-emerald-400'
                            : 'bg-red-900/40 text-red-400'
                        }`}
                      >
                        {item.status}
                      </span>
                    </td>
                    <td className="p-3 text-slate-400 text-xs">
                      {item.created_at
                        ? new Date(item.created_at).toLocaleDateString()
                        : '—'}
                    </td>
                    <td className="p-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {item.status === 'pending' && (
                        <div className="flex gap-2 justify-end">
                          <button
                            onClick={() => approveMutation.mutate({ id: item.id })}
                            disabled={approveMutation.isPending}
                            className="px-2 py-1 text-xs rounded bg-emerald-700/60 hover:bg-emerald-600 text-emerald-300 disabled:opacity-50"
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => rejectMutation.mutate({ id: item.id })}
                            disabled={rejectMutation.isPending}
                            className="px-2 py-1 text-xs rounded bg-red-900/40 hover:bg-red-800/60 text-red-400 disabled:opacity-50"
                          >
                            Reject
                          </button>
                        </div>
                      )}
                      {item.status !== 'pending' && item.review_note && (
                        <span className="text-slate-500 text-xs italic">{item.review_note}</span>
                      )}
                    </td>
                  </tr>
                  {expanded.has(item.id) && (
                    <tr className="border-b border-slate-700/50 bg-slate-900/40">
                      <td colSpan={8} className="p-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                          {item.technical_rationale && (
                            <div>
                              <p className="text-slate-400 text-xs font-medium mb-1">
                                Technical Rationale
                              </p>
                              <p className="text-slate-200">{item.technical_rationale}</p>
                            </div>
                          )}
                          {item.thesis && (
                            <div>
                              <p className="text-slate-400 text-xs font-medium mb-1">Thesis</p>
                              <p className="text-slate-200">{item.thesis}</p>
                            </div>
                          )}
                          {item.catalyst && (
                            <div>
                              <p className="text-slate-400 text-xs font-medium mb-1">Catalyst</p>
                              <p className="text-slate-200">{item.catalyst}</p>
                            </div>
                          )}
                          {item.strategy_matched && (
                            <div>
                              <p className="text-slate-400 text-xs font-medium mb-1">
                                Strategy Matched
                              </p>
                              <p className="text-slate-200">{item.strategy_matched}</p>
                            </div>
                          )}
                          <div className="flex gap-6">
                            {item.entry_price != null && (
                              <span>
                                <span className="text-slate-400 text-xs">Entry </span>
                                <span className="text-white">{item.entry_price.toFixed(2)}</span>
                              </span>
                            )}
                            {item.stop_loss != null && (
                              <span>
                                <span className="text-slate-400 text-xs">SL </span>
                                <span className="text-red-400">{item.stop_loss.toFixed(2)}</span>
                              </span>
                            )}
                            {item.take_profit != null && (
                              <span>
                                <span className="text-slate-400 text-xs">TP </span>
                                <span className="text-emerald-400">
                                  {item.take_profit.toFixed(2)}
                                </span>
                              </span>
                            )}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default SignalQueue;
